import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Download, Shield, Clock, Check, FileText, AlertCircle, History } from "lucide-react";
import { format, parseISO } from "date-fns";

interface GDPRDataExportProps {
  clinicianId: string;
  clients: any[];
}

interface DataExportRequest {
  id: string;
  user_id: string;
  request_type: string;
  requested_data_types: string[];
  status: string;
  created_at: string;
  processed_at: string | null;
  expires_at: string | null;
  export_url: string | null;
  export_format: string;
  notes: string | null;
}

const DATA_CATEGORIES = [
  { id: "profile", label: "Profile Information", description: "Basic client details and contact info" },
  { id: "sessions", label: "Session Records", description: "All therapy session records and notes" },
  { id: "assessments", label: "Clinical Assessments", description: "PHQ-9, GAD-7, and other assessment results" },
  { id: "homework", label: "Homework & Progress", description: "Assigned homework and completion status" },
  { id: "messages", label: "Messages", description: "All clinician-client communications" },
  { id: "milestones", label: "Journey Milestones", description: "Treatment progress milestones" },
  { id: "formulations", label: "Case Formulations", description: "Clinical case formulations and treatment plans" },
  { id: "invoices", label: "Billing Records", description: "Invoices and payment history" }
];

export function GDPRDataExport({ clinicianId, clients }: GDPRDataExportProps) {
  const { toast } = useToast();
  const [requests, setRequests] = useState<DataExportRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [showRequestDialog, setShowRequestDialog] = useState(false);
  const [processing, setProcessing] = useState(false);
  
  const [selectedClient, setSelectedClient] = useState<string>("");
  const [requestType, setRequestType] = useState<string>("export");
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);

  useEffect(() => {
    fetchRequests();
  }, [clinicianId]);

  const fetchRequests = async () => {
    // Fetch requests for all clients of this clinician
    const clientIds = clients.map(c => c.client_id);
    if (clientIds.length === 0) {
      setLoading(false);
      return;
    }
    
    const { data, error } = await supabase
      .from("data_export_requests")
      .select("*")
      .in("user_id", clientIds)
      .order("created_at", { ascending: false });

    if (error) {
      toast({ title: "Error", description: "Failed to load export requests", variant: "destructive" });
    } else {
      setRequests(data || []);
    }
    setLoading(false);
  };

  const handleCreateRequest = async () => {
    if (!selectedClient || selectedCategories.length === 0) return;

    setProcessing(true);

    const { error } = await supabase.from("data_export_requests").insert({
      user_id: selectedClient,
      request_type: requestType,
      requested_data_types: selectedCategories,
      status: "pending",
      export_format: "json"
    });

    if (error) {
      toast({ title: "Error", description: "Failed to create export request", variant: "destructive" });
    } else {
      toast({ 
        title: "Request Created", 
        description: "Data export is being prepared. This may take a few minutes." 
      });
      setShowRequestDialog(false);
      setSelectedClient("");
      setSelectedCategories([]);
      
      // Simulate processing (in production this would be an async job)
      setTimeout(async () => {
        const { data: latestRequest } = await supabase
          .from("data_export_requests")
          .select("*")
          .eq("user_id", selectedClient)
          .order("created_at", { ascending: false })
          .limit(1)
          .single();
        
        if (latestRequest) {
          await supabase
            .from("data_export_requests")
            .update({ 
              status: "completed",
              processed_at: new Date().toISOString(),
              expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), // 7 days
              export_url: "#" // In production, this would be a real URL
            })
            .eq("id", latestRequest.id);
          
          fetchRequests();
        }
      }, 3000);
    }

    setProcessing(false);
  };

  const toggleCategory = (categoryId: string) => {
    setSelectedCategories(prev =>
      prev.includes(categoryId)
        ? prev.filter(c => c !== categoryId)
        : [...prev, categoryId]
    );
  };

  const selectAllCategories = () => {
    setSelectedCategories(DATA_CATEGORIES.map(c => c.id));
  };

  const getClientName = (userId: string) => {
    const client = clients.find(c => c.client_id === userId);
    return client?.client_profile?.display_name || "Unknown Client";
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "pending":
        return <Badge variant="outline" className="bg-amber-500/10 text-amber-500 border-amber-500/20">Processing</Badge>;
      case "completed":
        return <Badge variant="outline" className="bg-green-500/10 text-green-500 border-green-500/20">Ready</Badge>;
      case "expired":
        return <Badge variant="outline" className="bg-muted text-muted-foreground">Expired</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            GDPR Data Management
          </h3>
          <p className="text-sm text-muted-foreground">Export client data for compliance and portability requests</p>
        </div>
        <Dialog open={showRequestDialog} onOpenChange={setShowRequestDialog}>
          <DialogTrigger asChild>
            <Button className="gap-2">
              <Download className="h-4 w-4" />
              New Export Request
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Create Data Export Request</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Client</Label>
                <Select value={selectedClient} onValueChange={setSelectedClient}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select client" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map(client => (
                      <SelectItem key={client.id} value={client.client_id}>
                        {client.client_profile?.display_name || 'Client'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Request Type</Label>
                <Select value={requestType} onValueChange={setRequestType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="export">Data Export (Subject Access Request)</SelectItem>
                    <SelectItem value="portability">Data Portability</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <div className="flex items-center justify-between mb-3">
                  <Label>Data Categories</Label>
                  <Button variant="link" size="sm" onClick={selectAllCategories} className="h-auto p-0">
                    Select All
                  </Button>
                </div>
                <div className="space-y-3 max-h-60 overflow-y-auto">
                  {DATA_CATEGORIES.map(category => (
                    <div 
                      key={category.id} 
                      className="flex items-start gap-3 p-3 rounded-lg border border-border/50 hover:bg-muted/30"
                    >
                      <Checkbox
                        id={category.id}
                        checked={selectedCategories.includes(category.id)}
                        onCheckedChange={() => toggleCategory(category.id)}
                      />
                      <div className="flex-1">
                        <Label htmlFor={category.id} className="font-medium cursor-pointer">
                          {category.label}
                        </Label>
                        <p className="text-xs text-muted-foreground">{category.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 text-blue-500 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-blue-500">GDPR Compliance Note</p>
                    <p className="text-muted-foreground text-xs mt-1">
                      Data exports are available for download for 7 days. Ensure secure handling of exported data.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setShowRequestDialog(false)}>Cancel</Button>
                <Button 
                  onClick={handleCreateRequest} 
                  disabled={!selectedClient || selectedCategories.length === 0 || processing}
                  className="gap-2"
                >
                  {processing ? "Processing..." : "Create Request"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Info Cards */}
      <div className="grid md:grid-cols-3 gap-4">
        <Card className="bg-card/50 border-border/30">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-full bg-primary/10">
              <Shield className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">GDPR Compliant</p>
              <p className="text-lg font-semibold text-foreground">Encrypted Export</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card/50 border-border/30">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-full bg-green-500/10">
              <Check className="h-5 w-5 text-green-500" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Response Time</p>
              <p className="text-lg font-semibold text-foreground">Under 30 days</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card/50 border-border/30">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-full bg-amber-500/10">
              <History className="h-5 w-5 text-amber-500" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Audit Trail</p>
              <p className="text-lg font-semibold text-foreground">Full Logging</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Export Requests List */}
      {requests.length === 0 ? (
        <Card className="bg-card/50 border-border/30">
          <CardContent className="py-12 text-center">
            <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-foreground mb-2">No Export Requests</h3>
            <p className="text-muted-foreground mb-4">
              Create data export requests for GDPR compliance and client data portability
            </p>
            <Button onClick={() => setShowRequestDialog(true)} className="gap-2">
              <Download className="h-4 w-4" />
              Create First Request
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-card/50 border-border/30">
          <CardHeader>
            <CardTitle>Export History</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {requests.map(request => (
                <div 
                  key={request.id} 
                  className="flex items-center justify-between p-4 rounded-lg bg-background/50 border border-border/20"
                >
                  <div className="flex items-center gap-4">
                    <div className="p-2 rounded-full bg-primary/10">
                      <FileText className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-foreground">{getClientName(request.user_id)}</p>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <span>{request.request_type === "export" ? "Data Export" : "Data Portability"}</span>
                        <span>•</span>
                        <span>{request.requested_data_types?.length || 0} categories</span>
                        <span>•</span>
                        <span>{format(parseISO(request.created_at), "MMM d, yyyy")}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {getStatusBadge(request.status)}
                    {request.status === "completed" && request.export_url && (
                      <Button size="sm" className="gap-1">
                        <Download className="h-3 w-3" />
                        Download
                      </Button>
                    )}
                    {request.status === "pending" && (
                      <div className="flex items-center gap-1 text-sm text-muted-foreground">
                        <Clock className="h-3 w-3 animate-spin" />
                        Processing...
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
